import { FC, useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import { Box, Button, CircularProgress, Container, Typography, Drawer, Skeleton, IconButton, Tooltip, Divider } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import jwt_decode from 'jwt-decode';
import { fetchInvoiceById, fetchInvoicePaymentReceiptPreview, fetchInvoices, deletePaymentReceipt } from '../../store/invoiceStore/invoiceThunk';
import { deleteVendorPayment } from '../../store/CashOutflowStore/cashOutflowThunk';
import { AInvoice, InvoicePreviewPayload } from "../../utils/invoice.types";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Utils, VocuherCategory } from "../../utils/common-utils";
// import HomeHeader from "../SignIn/HomeHeader"; // logo hidden on the shared receipt view
import DialogShareInvoiceReciept from '../../components/DialogShareInvoiceReciept/DialogShareInvoiceReciept';
import ShareIcon from '@mui/icons-material/Share';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InvoiceNavigationBar, { NavigationFilters } from '../../components/Invoice/InvoiceNavigationBar';
import { PermissionIconButton } from '../../components/Permission';
import DeleteReceiptsDialog from '../../components/Invoice/DeleteReceiptsDialog';
import InvoicePaymentBreakdown, { DETAIL_GRID_SX, FULL_ROW_CLASS, RIGHT_CELL_CLASS, useInvoicePayments } from '../../components/Invoice/InvoicePaymentBreakdown';

const actionBtnStyle = { float: 'left', color: '#545454', backgroundColor: '#f5f5f5', border: 'none', height: 35, width: 35, marginTop: 0, marginRight: 20, marginLeft: -5 } as const;

// URL carries type=AR|AP (receipt links) or sales|purchase (invoice links) — normalise to VocuherCategory
const toVoucherCategory = (type: string | null): string => {
  if (type === 'AP' || type === VocuherCategory.PURCHASE || type === 'purchases') return VocuherCategory.PURCHASE;
  return VocuherCategory.SALE;
};
const toReceiptTypeParam = (vCategory: string): string => (vCategory === VocuherCategory.PURCHASE ? 'AP' : 'AR');

// Only invoices that have at least one payment can have a receipt — prev/next & search stay within these
const RECEIPT_STATUS_FILTER = '&status=PAID,PARTIALLY_PAID,OVER_PAID';
const RECEIPT_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'PAID', label: 'Paid' },
  { value: 'PARTIALLY_PAID', label: 'Partially Paid' },
  { value: 'OVER_PAID', label: 'Over Paid' },
];

const formatDate = (d: any): string => (d ? new Date(d).toLocaleDateString('en-IN') : 'N/A');
const statusColor = (status?: string): string => {
  if (status === 'PAID') return 'success.main';
  if (status === 'OVER_PAID') return 'info.main';
  return 'warning.main';
};
const statusLabel = (status?: string): string => (status || 'PENDING').replace(/_/g, ' ');
const toNumber = (v: any): number => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
// Drafts store finalAmount as 0, so 0 falls back to amount + charges (same rule as the listing).
const invoiceTotal = (inv: any): number =>
  toNumber(inv?.finalAmount) ? toNumber(inv.finalAmount) : toNumber(inv?.amount) + toNumber(Utils.additionalChargesSum(inv));

interface IInvoicePaymentReceiptPreviewProps {
  /** When given, these replace the URL params (used when the page is shown inside a drawer). */
  invoiceId?: string;
  templateId?: string;
  type?: string;
  /** Drawer mode: switching receipts updates local state instead of the URL. */
  embedded?: boolean;
}

// Payment Receipt preview page — same navigation toolbar as the invoice preview page.
const InvoicePaymentReceiptPreview: FC<IInvoicePaymentReceiptPreviewProps> = (props) => {
  const params = useParams();
  const invoiceId = props.invoiceId ?? params.invoiceId;
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const urlTemplateId = props.templateId ?? searchParams.get("templateId");
  // Kept in state so drawer mode can switch receipts without touching the URL
  const [templateId, setTemplateId] = useState<string | null>(urlTemplateId);
  useEffect(() => { setTemplateId(urlTemplateId); }, [urlTemplateId]);
  const voucherType = toVoucherCategory(props.type ?? searchParams.get("type"));

  const { isLoggedIn, selectedAccount } = useAppSelector((state) => state.auth);
  const invoiceDetails = useAppSelector(state => state.invoices.invoiceDetails);

  const [pdfLocation, setPdfLocation] = useState<string>("");
  const [invoicePreview, setInvoicePreviewResponse] = useState<any>();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefersh, setIsRefersh] = useState("");
  const [currentInvoiceId, setCurrentInvoiceId] = useState(invoiceId);
  // Latest invoice id for async callbacks (list fetch resolves after the id may have changed)
  const currentInvoiceIdRef = useRef(currentInvoiceId);
  currentInvoiceIdRef.current = currentInvoiceId;

  // Payments on this invoice for the breakdown (logged-in only: the shared link has no auth).
  const { payments, loading: paymentsLoading } = useInvoicePayments(isLoggedIn ? currentInvoiceId : undefined, voucherType === VocuherCategory.PURCHASE, isRefersh);

  // Navigation window. The list endpoint is paged and the store's `invoices` is replaced on every
  // fetch, so the pages walked through are accumulated here. `indexOffset` is the absolute position
  // of windowInvoices[0] in the full filtered list, which is what makes the counter read "45 of 106"
  // and lets navigation continue from the receipt that was opened.
  const [windowInvoices, setWindowInvoices] = useState<AInvoice[]>([]);
  const [indexOffset, setIndexOffset] = useState(0);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  // First load of the navigation list; the toolbar renders right away and only prev/next wait for it
  const [listLoading, setListLoading] = useState(true);
  const [filters, setFilters] = useState<NavigationFilters>({
    status: 'ALL', dateFrom: null, dateTo: null, partyName: '', amountMin: '', amountMax: '',
  });
  const pageSize = 20;

  const loadedEnd = indexOffset + windowInvoices.length;
  const hasMoreData = totalCount === undefined ? true : loadedEnd < totalCount;
  const hasPrevData = indexOffset > 0;

  /** Filter values the list endpoint understands; empty ones are left off the query. */
  const buildFilterParams = (f: NavigationFilters) => {
    // Receipts are always restricted to the paid statuses unless one of them is picked explicitly.
    const parts: string[] = [
      f.status && f.status !== 'ALL'
        ? `&status=${encodeURIComponent(f.status)}`
        : RECEIPT_STATUS_FILTER,
    ];
    // `start`/`end` are only honoured together, so a one-sided range is widened to the other end.
    if (f.dateFrom || f.dateTo) {
      const from = f.dateFrom || new Date('1970-01-01');
      const to = f.dateTo || new Date();
      parts.push(`&start=${from.toISOString()}&end=${to.toISOString()}`);
    }
    if (f.partyName.trim()) parts.push(`&partyName=${encodeURIComponent(f.partyName.trim())}`);
    if (f.amountMin || f.amountMax) {
      parts.push(`&amountStartRange=${f.amountMin || 0}&amountEndRange=${f.amountMax || Number.MAX_SAFE_INTEGER}`);
    }
    return parts.join('');
  };

  const [openShareInvoiceRecieptDrawer, setOpenShareInvoiceRecieptDrawer] = useState<boolean>(false);
  const [openDeleteReceiptsDialog, setOpenDeleteReceiptsDialog] = useState<boolean>(false);
  const [isDeletingReceipts, setIsDeletingReceipts] = useState<boolean>(false);

  const openShareInvoiceDrawer = (event: any) => {
    setOpenShareInvoiceRecieptDrawer(true);
    event.stopPropagation();
  };

  const handleCloseDrawer = () => {
    setOpenShareInvoiceRecieptDrawer(false);
  };

  // Back to the invoice preview of the current invoice
  const handleViewInvoice = () => {
    if (!currentInvoiceId) return;
    const invTemplate = invoiceDetails?.pdfTemplate || '';
    navigate(`/invoice/${currentInvoiceId}/preview?templateId=${invTemplate}&type=${voucherType}`);
  };

  // Delete all receipts applied to the current invoice (same thunks as the action menu)
  const handleConfirmDeleteReceipts = async () => {
    if (!currentInvoiceId) return;
    setIsDeletingReceipts(true);
    try {
      const vCategory = invoiceDetails?.voucherCategory || voucherType;
      // unwrap: a failed delete must not leave this page (the slice already toasts the error).
      if (vCategory === VocuherCategory.SALE) {
        await dispatch(deletePaymentReceipt(currentInvoiceId)).unwrap();
      } else {
        await dispatch(deleteVendorPayment(currentInvoiceId)).unwrap();
      }
      setOpenDeleteReceiptsDialog(false);
      // The receipt no longer exists: go to its invoice, which reloads the pending balance and rebuilt PDF.
      const invTemplate = invoiceDetails?.pdfTemplate || '';
      navigate(`/invoice/${currentInvoiceId}/preview?templateId=${invTemplate}&type=${voucherType}`, { replace: true });
    } catch (error) {
      console.error('Error deleting receipts:', error);
      setOpenDeleteReceiptsDialog(false);
    } finally {
      setIsDeletingReceipts(false);
    }
  };

  // Receipt-only toolbar actions
  const receiptActions = (
    <>
      {/* First icon gets the same left gap as the invoice preview so it clears the divider. */}
      <IconButton onClick={handleViewInvoice} style={{ ...actionBtnStyle, marginLeft: 20 }} size='small'>
        <Tooltip arrow title={`View ${invoiceDetails?.voucherType || 'Invoice'}`}><DescriptionIcon color="info" style={{ fontSize: 20 }} /></Tooltip>
      </IconButton>
      <IconButton onClick={(e) => openShareInvoiceDrawer(e)} style={actionBtnStyle} size='small'>
        <Tooltip arrow title="Share Receipt"><ShareIcon color="success" style={{ fontSize: 20 }} /></Tooltip>
      </IconButton>
      <PermissionIconButton module="payments" action="delete"
        title="Delete All Receipts"
        deniedMessage="You do not have permission to delete payment receipts"
        onClick={() => setOpenDeleteReceiptsDialog(true)}
        style={actionBtnStyle}
        size='small'>
        <ReceiptLongIcon color="error" style={{ fontSize: 20 }} />
      </PermissionIconButton>
    </>
  );

  // Update currentInvoiceId when URL params change
  useEffect(() => {
    if (invoiceId && invoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(invoiceId);
      setIsLoading(true);
    }
  }, [invoiceId]);

  /** One page of the list endpoint, with the active filters applied server-side. */
  const fetchPage = async (page: number, locateInvoiceId?: string) => {
    const { _id: accountId } = selectedAccount;
    if (!accountId || !isLoggedIn) return null;
    // The endpoint pages by `page` (0-based) x `limit` — it ignores `skip`.
    const locate = locateInvoiceId ? `&locateInvoiceId=${locateInvoiceId}` : '';
    const result: any = await dispatch(fetchInvoices({
      accountId,
      type: voucherType,
      params: `&page=${page}&limit=${pageSize}${buildFilterParams(filters)}${locate}`,
    }));
    // fetchInvoices.fulfilled resets invoiceDetails in the slice — reload the current
    // invoice's details so the sidebar breakdown isn't wiped by the list response.
    if (currentInvoiceIdRef.current) {
      dispatch(fetchInvoiceById(currentInvoiceIdRef.current));
    }
    return result.payload || null;
  };

  /**
   * Builds the navigation window around the receipt that is open: asks the server where it sits in
   * the filtered list, then loads the page holding it, so navigation continues from there instead
   * of restarting at the newest 20.
   */
  const loadWindowAroundCurrent = async () => {
    setListLoading(true);
    try {
      await loadWindow();
    } finally {
      setListLoading(false);
    }
  };

  const loadWindow = async () => {
    const probe = await fetchPage(0, currentInvoiceIdRef.current);
    if (!probe) return;
    setTotalCount(typeof probe.total === 'number' ? probe.total : undefined);

    const position = typeof probe.currentIndex === 'number' ? probe.currentIndex : null;
    const targetPage = position === null ? 0 : Math.floor(position / pageSize);

    let rows: AInvoice[] = probe.invoices || [];
    let offset = 0;
    if (targetPage > 0) {
      const page = await fetchPage(targetPage);
      if (page?.invoices?.length) {
        rows = page.invoices;
        offset = targetPage * pageSize;
      }
    }
    setWindowInvoices(rows);
    setIndexOffset(offset);
  };

  // Initial load, and a reload whenever the account, voucher type or filters change.
  useEffect(() => {
    loadWindowAroundCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount._id, isLoggedIn, voucherType, filters]);


  // Load current invoice details (toolbar actions + sidebar breakdown) when ID changes
  useEffect(() => {
    if (currentInvoiceId && isLoggedIn) {
      dispatch(fetchInvoiceById(currentInvoiceId));
    }
  }, [currentInvoiceId, dispatch, isLoggedIn]);

  // Load the receipt PDF
  useEffect(() => {
    // Stops a pending receipt wait once the user moves to another invoice.
    let cancelled = false;
    const getPreview = async () => {
      let reqObj: InvoicePreviewPayload = { invoiceId: currentInvoiceId, templateId: templateId };
      let invoicePreviewResponse: any;
      // A just-taken payment's receipt is built in the background: keep loading (up to ~60s) rather than show an old one.
      for (let tries = 0; ; tries++) {
        invoicePreviewResponse = await dispatch(fetchInvoicePaymentReceiptPreview(reqObj));
        if (cancelled) return;
        if (!invoicePreviewResponse?.payload?.pdfStale || tries >= 20) break;
        setIsLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 3000));
        if (cancelled) return;
      }

      let location = null;
      if (invoicePreviewResponse?.payload && !invoicePreviewResponse?.error && !invoicePreviewResponse.payload.pdfStale) {
        if (invoicePreviewResponse.payload.length > 0) {
          location = invoicePreviewResponse.payload[0].Location;
        } else {
          location = invoicePreviewResponse.payload.Location;
        }
        setInvoicePreviewResponse(invoicePreviewResponse.payload);
      } else {
        setInvoicePreviewResponse(undefined);
      }
      setPdfLocation(location || '');
      setIsLoading(false);
    };

    if (templateId && currentInvoiceId) {
      getPreview();
    } else if (currentInvoiceId) {
      try {
        let token: any = jwt_decode(currentInvoiceId);
        setPdfLocation(token.pdfLcation);
        setIsLoading(false);
      } catch (error) {
        console.log("Failed! Invalid Token", error);
        setIsLoading(false);
      }
    }
    return () => { cancelled = true; };
  }, [currentInvoiceId, isRefersh, templateId, dispatch]);

  const actionChangeHandler = (data: any) => {
    setIsRefersh(data.isRefersh);
  };

  // Navigate to another invoice's receipt (prev / next / search)
  const handleInvoiceChange = (newInvoiceId: string, _newTemplateId: string, vCategory: string) => {
    if (newInvoiceId !== currentInvoiceId) {
      setCurrentInvoiceId(newInvoiceId);
      setIsLoading(true);
      setPdfLocation('');

      if (isLoggedIn) {
        dispatch(fetchInvoiceById(newInvoiceId));
      }

      // Receipts use the invoice's receipt template, not the invoice template
      const target: any = windowInvoices?.find((inv: any) => inv._id === newInvoiceId);
      const receiptTemplateId = target?.receiptPdfTemplate || templateId || '';
      const newCategory = vCategory === VocuherCategory.PURCHASE ? VocuherCategory.PURCHASE : VocuherCategory.SALE;

      setTemplateId(receiptTemplateId);
      if (!props.embedded) {
        navigate(`/invoice-payment-receipt/${newInvoiceId}/view?templateId=${receiptTemplateId}&type=${toReceiptTypeParam(newCategory)}`, {
          replace: true
        });
      }
    }
  };

  /** Append the next page so navigation can keep moving forward through all matching receipts. */
  const handleLoadMore = async () => {
    if (!hasMoreData || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await fetchPage(Math.floor(loadedEnd / pageSize));
      const rows: AInvoice[] = next?.invoices || [];
      if (rows.length) {
        const known = new Set(windowInvoices.map(i => i._id));
        setWindowInvoices(prev => [...prev, ...rows.filter(r => !known.has(r._id))]);
      }
      if (typeof next?.total === 'number') setTotalCount(next.total);
    } finally {
      setLoadingMore(false);
    }
  };

  /** Prepend the previous page when the user navigates back past the start of the window. */
  const handleLoadPrevious = async () => {
    if (!hasPrevData || loadingMore) return;
    setLoadingMore(true);
    try {
      const prevPage = Math.max(0, Math.floor(indexOffset / pageSize) - 1);
      const prev = await fetchPage(prevPage);
      const rows: AInvoice[] = prev?.invoices || [];
      if (rows.length) {
        const known = new Set(windowInvoices.map(i => i._id));
        setWindowInvoices(current => [...rows.filter(r => !known.has(r._id)), ...current]);
        setIndexOffset(prevPage * pageSize);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // Direct invoice-number search (the "#" button)
  const handleDirectInvoiceSearch = async (invoiceNumber: string): Promise<boolean> => {
    if (!invoiceNumber.trim()) return false;

    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      try {
        const result: any = await dispatch(fetchInvoices({
          accountId,
          type: voucherType,
          params: `&invoiceNumber=${encodeURIComponent(invoiceNumber)}${RECEIPT_STATUS_FILTER}`
        }));
        const foundInvoices = result.payload?.invoices || [];

        if (foundInvoices.length > 0) {
          // The matches become the whole navigable set until the search is cleared.
          setWindowInvoices(foundInvoices);
          setIndexOffset(0);
          setTotalCount(foundInvoices.length);
          const firstInvoice = foundInvoices[0];
          await dispatch(fetchInvoiceById(firstInvoice._id));
          handleInvoiceChange(
            firstInvoice._id,
            firstInvoice.receiptPdfTemplate || templateId || '',
            firstInvoice.voucherCategory || voucherType
          );
          return true;
        } else {
          alert(`No paid invoices found matching "${invoiceNumber}". Please try a different search.`);
          return false;
        }
      } catch (error) {
        console.error('Error searching invoice:', error);
        alert('Error searching for invoice. Please try again.');
        return false;
      }
    }
    return false;
  };

  // Logged-out (shared link) view — unchanged
  if (!isLoggedIn) {
    return (
      isLoading ?
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
        :
        <Grid container rowSpacing="24" columnSpacing="5">
          <Container component="main" >
            <div style={{ "width": "70vw" }}>
              {/* Logo + Login/Signup hidden on the shared (email / WhatsApp) receipt link view
              <div className="preview">
                <HomeHeader />
              </div>
              <Box onClick={() => { navigate("/") }}
                style={{ "float": "right", "marginTop": "-42" }}
                sx={{ cursor: "pointer" }}>
                <Button disableElevation sx={{ mt: "20px" }} size="large" type="submit" variant="contained" color="primary">
                  Login/Signup
                </Button>
              </Box>
              */}
              <p style={{ "clear": "both" }} />
            </div>
            <p style={{ "clear": "both" }}></p>
            <Box>
              <object style={{ height: "80vh", width: "70vw" }} data={pdfLocation} type="application/pdf" />
            </Box>
          </Container>
        </Grid>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: '#fafafb', minHeight: props.embedded ? '100%' : '100vh', pt: 0, pb: 2 }}>
        {/* Navigation Bar and Actions — same toolbar as invoice preview */}
        <Box sx={{ p: { xs: 1, sm: 2 }, pb: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              {(!props.embedded && currentInvoiceId) && (
                <InvoiceNavigationBar
                  currentInvoiceId={currentInvoiceId || ''}
                  voucherCategory={invoiceDetails?.voucherCategory || ''}
                  voucherType={voucherType}
                  onInvoiceChange={handleInvoiceChange}
                  invoices={windowInvoices}
                  currentInvoiceDetails={invoiceDetails}
                  onActionChange={actionChangeHandler}
                  onLoadMore={handleLoadMore}
                  onLoadPrevious={handleLoadPrevious}
                  hasMoreData={hasMoreData}
                  hasPrevData={hasPrevData}
                  loadingMore={loadingMore || listLoading}
                  totalCount={totalCount}
                  indexOffset={indexOffset}
                  onFiltersChange={setFilters}
                  onDirectSearch={handleDirectInvoiceSearch}
                  actions={receiptActions}
                  entityLabel="Receipt"
                  statusOptions={RECEIPT_STATUS_OPTIONS}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} sx={{ p: { xs: 1, sm: 2 } }}>
          <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 }, mt: { xs: 2, md: 0 } }}>
            {(isLoading ?
              <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', p: 3, height: { xs: '60vh', md: '85vh' } }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info" />
                    <Typography sx={{ mt: 2 }}>Loading Preview ...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
              :
              <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', height: { xs: '60vh', md: '85vh' } }}>
                {pdfLocation ? (
                  <object style={{ height: "100%", width: "100%" }} data={pdfLocation} type="application/pdf" />
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                    <ReceiptLongIcon sx={{ fontSize: 48, color: '#9ca3af', mb: 1 }} />
                    <Typography color="text.secondary">No payment receipt available for this invoice</Typography>
                    <Typography variant="caption" color="text.secondary">Record a payment first to generate a receipt</Typography>
                  </Box>
                )}
              </Box>
            )}
          </Grid>

          {/* Right side - Receipt Details (same breakdown as invoice preview) */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>

            {/* Receipt / Party Details */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #e9ecf1',
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{
                bgcolor: '#f5f5f5',
                p: { xs: 1.5, sm: 2 },
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                  Payment Receipt Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                  {formatDate(invoiceDetails?.date)} • #{invoiceDetails?.invoiceNumber || ''}
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box sx={DETAIL_GRID_SX}>

                  <Box className={FULL_ROW_CLASS}>
                    <Typography variant="body2" color="text.secondary">Party Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invoiceDetails?.buyer?.name || invoiceDetails?.supplier?.name || invoiceDetails?.client?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  <Box className={FULL_ROW_CLASS}>
                    <Typography variant="body2" color="text.secondary">Invoice Amount</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                      {Utils.formatNumber(invoiceTotal(invoiceDetails).toFixed(2))}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                    <Typography variant="body1">{formatDate(invoiceDetails?.date)}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Invoice Type</Typography>
                    <Typography variant="body1">{invoiceDetails?.voucherType || 'N/A'}</Typography>
                  </Box>

                  <Box className={RIGHT_CELL_CLASS}>
                    <Typography variant="body2" color="text.secondary">Payment Status</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: statusColor(invoiceDetails?.status) }}>
                      {statusLabel(invoiceDetails?.status)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Received Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                      {Utils.formatNumber(toNumber(invoiceDetails?.receivedAmount).toFixed(2))}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">Balance Amount</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: toNumber(invoiceDetails?.balanceAmount) > 0 ? 'warning.main' : 'text.primary' }}>
                      {Utils.formatNumber(toNumber(invoiceDetails?.balanceAmount).toFixed(2))}
                    </Typography>
                  </Box>

                  {/* Received / Balance are already listed above, so only the payment rows here. */}
                  <InvoicePaymentBreakdown payments={payments} loading={paymentsLoading} showTotals={false} />

                </Box>
              </Box>
            </Box>

          </Grid>
        </Grid>
      </Box>

      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        onClose={handleCloseDrawer}
        open={openShareInvoiceRecieptDrawer}>
        <DialogShareInvoiceReciept
          dispatchType="Payment Receipt"
          invoiceId={currentInvoiceId || ''}
          invoicePreviewData={invoicePreview}
          selectedBuyer={invoiceDetails?.client || invoiceDetails?.buyer || invoiceDetails?.supplier || {}}
          onClose={handleCloseDrawer}
          openDrawer={openShareInvoiceRecieptDrawer}>
        </DialogShareInvoiceReciept>
      </Drawer>

      {/* Delete Receipts Confirmation */}
      <DeleteReceiptsDialog
        open={openDeleteReceiptsDialog}
        invoiceNumber={invoiceDetails?.invoiceNumber || ''}
        moduleLabel={invoiceDetails?.voucherType || 'Invoice'}
        deleting={isDeletingReceipts}
        onClose={() => setOpenDeleteReceiptsDialog(false)}
        onConfirm={handleConfirmDeleteReceipts}
      />
    </>
  );
};

export default InvoicePaymentReceiptPreview;
